import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { SubmissionBrokerageDial } from '@/components/submission/BrokerageDial';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AIRAMark } from '@/components/brand/Marks';
import {
  Building2, CheckCircle, XCircle, AlertTriangle,
  ChevronDown, ChevronUp, FileText, Lightbulb, Copy
} from 'lucide-react';
import { RegistryLink } from '@/components/RegistryLink';
import { ScoreGauge } from '@/components/submission/ScoreGauge';
import AsyncNarrative from '@/components/submission/AsyncNarrative';
import { NextStepCard } from '@/components/submission/NextStepCard';
import { BindCelebration } from '@/components/BindCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DECISION_STYLES = {
  APPROVE: { bg: '#1D9E75', icon: CheckCircle, label: 'Approved', desc: 'This submission has been approved for binding. STP - no human review required.' },
  CONDITIONS: { bg: '#00B4D8', icon: CheckCircle, label: 'Approved with Conditions', desc: 'Approved subject to conditions. Review conditions before binding.' },
  REFER: { bg: '#E8A020', icon: AlertTriangle, label: 'Referred for Review', desc: 'This submission has been referred to an underwriter for manual review.' },
  DECLINE: { bg: '#E24B4A', icon: XCircle, label: 'Declined', desc: 'This submission has been declined. See override rules for details.' },
};

export function DimensionBar({ dim, data }) {
  let barColor = '#1D9E75';
  if (data.risk_factor > 0.6) barColor = '#E24B4A';
  else if (data.risk_factor > 0.35) barColor = '#E8A020';

  return (
    <div className="flex items-center gap-3">
      <span className="w-6 text-xs font-mono font-bold" style={{ color: '#1A4C7C' }}>{dim}</span>
      <div className="flex-1">
        <div className="flex justify-between text-[11px] mb-0.5">
          <span style={{ color: '#0D1B2A' }}>{data.name}</span>
          <span className="font-mono" style={{ color: '#5C6B7A' }}>{data.score}/{data.max_points}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
          <div className="h-full rounded-full transition-all duration-500"
            style={{ width: `${(data.score / data.max_points) * 100}%`, background: barColor }} />
        </div>
      </div>
    </div>
  );
}

export default function ScoringResult({ result, onReset, onUpdated }) {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useAuth();
  const isStaff = user?.role && user.role !== 'broker';
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showExplain, setShowExplain] = useState(false);
  const [celebrate, setCelebrate] = useState(() => !!result.policy_id);

  const sr = result.scoring_result;
  const [quote, setQuote] = useState(sr.marais_quote);
  useEffect(() => { setQuote(sr.marais_quote); }, [sr.marais_quote]);
  const returned = result.status === 'info_requested' && result.broker_action;
  const ds = returned
    ? { bg: '#E8A020', icon: AlertTriangle, label: result.broker_action === 'answer' ? 'Back to you — answers needed' : 'Back to you — underwriter needed', desc: result.broker_action === 'answer' ? 'Answer below and the quote re-prices on the spot.' : 'Refer it to an underwriter, or correct the details and re-price — your call.' }
    : result.policy_id ? { ...(DECISION_STYLES[sr.decision] || DECISION_STYLES.APPROVE), label: `${(DECISION_STYLES[sr.decision] || DECISION_STYLES.APPROVE).label} & bound`, desc: 'Bound straight through — the documents are issued.' }
    : (DECISION_STYLES[sr.decision] || DECISION_STYLES.REFER);
  const DIcon = ds.icon;

  const sortedDimensions = useMemo(() => {
    if (!sr?.dimension_scores) return [];
    return Object.entries(sr.dimension_scores).sort((a, b) => b[1].score - a[1].score);
  }, [sr]);

  return (
    <div data-testid="scoring-result" className="max-w-3xl mx-auto space-y-6">
      {/* Decision Banner */}
      <Card className="rounded-sm overflow-hidden border-0">
        <div className="p-6 text-white" style={{ background: ds.bg }}>
          <div className="flex items-center gap-3">
            <DIcon className="w-8 h-8" />
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{ds.label}</h2>
              <p className="text-white/80 text-sm mt-1">{ds.desc}</p>
            </div>
          </div>
        </div>
        <div className="p-6 bg-white">
          <ScoreGauge sr={sr} premium={sr.marais_quote?.total_payable} />
          {celebrate && result.policy_id && (
            <BindCelebration policyId={result.policy_id} policyNumber={result.policy_number} seconds={sr.decision_seconds} onClose={() => setCelebrate(false)} />
          )}
          <div className="grid grid-cols-3 gap-4 mt-5 pt-4 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
            <div title={sr.confidence_detail ? `Method: ${sr.confidence_detail.method}\nDecision margin: ${Math.round(sr.confidence_detail.components.decision_margin * 100)}% · Dimension consensus: ${Math.round(sr.confidence_detail.components.dimension_consensus * 100)}% · Data completeness: ${Math.round(sr.confidence_detail.components.data_completeness * 100)}%` : undefined}>
              <p className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Confidence</p>
              <p className="text-2xl font-bold mt-1" style={{ color: '#0D1B2A' }} data-testid="confidence-value">{(sr.confidence * 100).toFixed(0)}%</p>
              {sr.confidence_detail && (
                <p className="text-[10px] capitalize" style={{ color: sr.confidence_detail.label === 'high' ? '#1D9E75' : sr.confidence_detail.label === 'medium' ? '#E8A020' : '#E24B4A' }} data-testid="confidence-label">
                  {sr.confidence_detail.label} model certainty
                </p>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>Data Completeness</p>
              <p className="text-2xl font-bold mt-1" style={{ color: '#0D1B2A' }}>{sr.data_completeness_pct}%</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: '#5C6B7A' }}>STP</p>
              <p className="text-2xl font-bold mt-1" style={{ color: sr.stp ? '#00B4D8' : '#5C6B7A' }}>{sr.stp ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>
      </Card>

      <NextStepCard result={result} isStaff={!!isStaff} onUpdated={onUpdated} />

      {/* Business Info */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-4 h-4" style={{ color: '#1A4C7C' }} />
          <span className="font-semibold text-sm" style={{ color: '#0D1B2A' }}>{result.business_name}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs" style={{ color: '#5C6B7A' }}>
          {result.abn ? <RegistryLink abn={result.abn} prefix /> : <span>ABN: N/A</span>}
          <span>Policy: {result.policy_id ? 'Generated' : 'Pending'}</span>
          <span>Score Version: {sr.score_version}</span>
          <span className="flex items-center gap-1" data-testid="submission-id-display">
            Submission: <span className="font-mono">{result.client_ref || result.submission_id?.slice(0, 8)}</span>
            <button title="Copy full submission ID" onClick={() => navigator.clipboard?.writeText(result.submission_id || '')}
              className="p-0.5 rounded hover:bg-gray-100"><Copy className="w-3 h-3" /></button>
          </span>
        </div>
      </Card>

      {/* Duplicate warning */}
      {result.duplicate_warning?.count > 0 && (
        <Card className="rounded-sm border-l-4 bg-white p-4" style={{ borderLeftColor: '#E8A020' }} data-testid="duplicate-warning">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4" style={{ color: '#E8A020' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#B27516' }}>
              Possible duplicate — {result.duplicate_warning.count} other submission{result.duplicate_warning.count === 1 ? '' : 's'} for this entity in the last 30 days
            </span>
          </div>
          <div className="space-y-0.5 text-xs" style={{ color: '#5C6B7A' }}>
            {result.duplicate_warning.recent.map(d => (
              <p key={d.submission_id}>
                <span className="font-mono">{d.submission_id?.slice(0, 8)}</span> · {d.decision || d.status} · {d.created_at ? new Date(d.created_at).toLocaleDateString('en-AU') : ''}{d.broker_name ? ` · ${d.broker_name}` : ''}
              </p>
            ))}
          </div>
        </Card>
      )}

      {/* Corporate Group Linkage */}
      {result.corporate_group && (
        <Card className="rounded-sm border-l-4 bg-white p-4" style={{ borderLeftColor: '#1A4C7C' }} data-testid="corporate-group-link">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4" style={{ color: '#1A4C7C' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#1A4C7C' }}>Corporate Group</span>
          </div>
          <p className="text-sm font-bold" style={{ color: '#0D1B2A' }}>{result.corporate_group.group_name}</p>
          <div className="grid grid-cols-3 gap-3 mt-2 text-xs" style={{ color: '#5C6B7A' }}>
            <span><strong style={{ color: '#0D1B2A' }}>{result.corporate_group.entity_count?.toLocaleString()}</strong> entities in group</span>
            <span><strong style={{ color: '#0D1B2A' }}>{result.corporate_group.total_employees?.toLocaleString()}</strong> total employees</span>
            <span><strong style={{ color: '#0D1B2A' }}>{result.corporate_group.city_count}</strong> locations</span>
          </div>
          {result.corporate_group.sub_entity_types?.length > 0 && (
            <div className="mt-2 flex gap-1">
              {result.corporate_group.sub_entity_types.map(t => (
                <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }}>{t}</span>
              ))}
            </div>
          )}
          <p className="text-[10px] mt-2 italic" style={{ color: '#5C6B7A' }}>Large corporate groups are typically underwritten manually with bespoke terms.</p>
        </Card>
      )}

      {/* Override Rules */}
      {sr.override_rules_triggered?.length > 0 && (
        <Card className="rounded-sm border-l-4 p-4 bg-white" style={{ borderLeftColor: '#E8A020' }}>
          <h3 className="text-sm font-semibold mb-2" style={{ color: '#E8A020' }}>Override Rules Triggered</h3>
          <ul className="space-y-1">
            {sr.override_rules_triggered.map((r) => (
              <li key={r.rule} className="text-xs flex items-center gap-2" style={{ color: '#0D1B2A' }}>
                <AlertTriangle className="w-3 h-3" style={{ color: '#E8A020' }} />
                <span className="font-mono text-[10px] px-1 rounded" style={{ background: 'rgba(232,160,32,0.1)', color: '#E8A020' }}>{r.rule}</span>
                {r.desc} — <span className="font-semibold">{r.action}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Itemised Premium Quote + the broker's brokerage dial (unbound only) */}
      {quote && <PremiumQuoteCard q={quote} />}
      {quote?.gross_premium != null && !result.policy_id && result.submission_id && (
        <SubmissionBrokerageDial api={api} submissionId={result.submission_id} quote={quote} onChange={setQuote} />
      )}

      {/* Score Breakdown */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <button data-testid="toggle-breakdown" onClick={() => setShowBreakdown(!showBreakdown)}
          className="w-full p-4 flex items-center justify-between hover:bg-[#F5F7FA] transition-colors">
          <span className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Dimension Score Breakdown</span>
          {showBreakdown ? <ChevronUp className="w-4 h-4" style={{ color: '#5C6B7A' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#5C6B7A' }} />}
        </button>
        {showBreakdown && (
          <div className="px-4 pb-4 space-y-2">
            {sortedDimensions.map(([dim, data]) => (
              <DimensionBar key={dim} dim={dim} data={data} />
            ))}
          </div>
        )}
      </Card>

      {/* Explain Score */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <button data-testid="explain-score-btn" onClick={() => setShowExplain(!showExplain)}
          className="w-full p-4 flex items-center justify-between hover:bg-[#F5F7FA] transition-colors">
          <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#0D1B2A' }}>
            <Lightbulb className="w-4 h-4" style={{ color: '#E8A020' }} />Explain Score
          </span>
          {showExplain ? <ChevronUp className="w-4 h-4" style={{ color: '#5C6B7A' }} /> : <ChevronDown className="w-4 h-4" style={{ color: '#5C6B7A' }} />}
        </button>
        {showExplain && <ExplainPanel sr={sr} />}
      </Card>

      {/* AI Narrative (async — streams in after the decision) */}
      <AsyncNarrative sr={sr} submissionId={result.submission_id} />

      {/* Document Downloads */}
      {result.policy_id && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-4" data-testid="document-downloads">
          <h3 className="text-sm font-semibold mb-3" style={{ color: '#0D1B2A' }}>Documents</h3>
          <div className="flex gap-3">
            <a href={`${BACKEND_URL}/api/documents/policy-schedule/${result.policy_id}`}
              target="_blank" rel="noopener noreferrer" data-testid="download-policy-schedule"
              className="px-4 py-2 rounded-sm text-xs font-semibold text-white flex items-center gap-2 hover:opacity-90 transition-opacity"
              style={{ background: '#1A4C7C' }}>
              <FileText className="w-3.5 h-3.5" /> Policy Schedule
            </a>
            <a href={`${BACKEND_URL}/api/documents/certificate/${result.policy_id}`}
              target="_blank" rel="noopener noreferrer" data-testid="download-certificate"
              className="px-4 py-2 rounded-sm text-xs font-semibold text-white flex items-center gap-2 hover:opacity-90 transition-opacity"
              style={{ background: '#1D9E75' }}>
              <FileText className="w-3.5 h-3.5" /> Certificate of Currency
            </a>
          </div>
        </Card>
      )}

      {result.decision === 'DECLINE' && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-4" data-testid="decline-documents">
          <h3 className="text-sm font-semibold mb-3" style={{ color: '#0D1B2A' }}>Documents</h3>
          <a href={`${BACKEND_URL}/api/documents/declinature/${result.submission_id}`}
            target="_blank" rel="noopener noreferrer" data-testid="download-declinature"
            className="px-4 py-2 rounded-sm text-xs font-semibold text-white inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
            style={{ background: '#E24B4A' }}>
            <FileText className="w-3.5 h-3.5" /> Declinature Letter
          </a>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button data-testid="new-submission-btn" onClick={onReset} className="rounded-sm" style={{ background: '#1A4C7C' }}>New Submission</Button>
        <Button data-testid="view-quote-details-btn" variant="outline" onClick={() => navigate(`/submissions/${result.submission_id}`)} className="rounded-sm">Full Quote Details</Button>
        <Button data-testid="view-submissions-btn" variant="outline" onClick={() => navigate('/submissions')} className="rounded-sm">View All Submissions</Button>
      </div>
    </div>
  );
}

const fmt$ = (v) => v == null ? '—' : `$${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Row({ label, value, bold, big, muted, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: muted ? '#64748B' : '#5C6B7A' }}>{label}</span>
      <span className={`font-mono ${bold ? 'font-bold' : ''} ${big ? 'text-base' : ''}`}
        style={{ color: accent || '#0D1B2A' }}>{value}</span>
    </div>
  );
}

export function PremiumQuoteCard({ q }) {
  const l = q.loadings || {};
  const mods = [
    q.score_band && q.score_band.adjustment_pct !== 0 && {
      label: `Risk score band — ${q.score_band.label} (score ${Number(q.score_band.score).toLocaleString()})`,
      value: q.score_band.multiplier,
    },
    l.territory_multiplier != null && { label: `Location / Territory (${l.territory || 'N/A'})`, value: l.territory_multiplier },
    l.occupation_loading != null && { label: `Industry / Occupation (${l.occupation_tier || 'tier'})`, value: l.occupation_loading },
    l.construction_multiplier != null && { label: `Construction (${(l.construction || '').replace(/_/g, ' ')})`, value: l.construction_multiplier },
    l.claims_loading != null && { label: 'Claims history', value: l.claims_loading },
    l.fleet_discount_factor != null && l.fleet_discount_factor !== 1 && {
      label: `Fleet discount (${l.fleet_size} vehicles, ${Math.round((l.fleet_discount_pct || 0) * 100)}%)`,
      value: l.fleet_discount_factor,
    },
    l.risk_management_credit != null && l.risk_management_credit !== 1 && {
      label: `Risk management credit (${(l.risk_controls_applied || []).length} controls evidenced)`,
      value: l.risk_management_credit,
    },
    l.claims_factor != null && l.claims_factor !== 1 && {
      label: `Claims experience (${(l.claims_band || '').replace(/_/g, ' ')})`,
      value: l.claims_factor,
    },
    l.radius_adjust != null && l.radius_adjust !== 1 && { label: 'Operating radius', value: l.radius_adjust },
    l.ndp_loading != null && l.ndp_loading !== 1 && { label: 'Disclosure behaviour', value: l.ndp_loading },
    ...(l.hazard_detail || []).map(h => ({
      label: `Hazard — ${h.peril.charAt(0).toUpperCase() + h.peril.slice(1)} (${h.zone} zone)`,
      value: h.multiplier,
    })),
  ].filter(Boolean);
  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-5" data-testid="premium-quote-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: '#0D1B2A' }}>Premium Quote — Itemised</h3>
        <span className="text-[10px] font-mono" style={{ color: '#5C6B7A' }}>{q.version}</span>
      </div>
      <div className="space-y-1 text-xs">
        {(q.vehicle_lines || []).length > 0 && (
          <div className="mb-2 pb-1.5 border-b" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid="vehicle-lines">
            {q.vehicle_lines.map((v, i) => (
              <div key={i} className="flex items-center justify-between py-0.5" data-testid={`vehicle-line-${i}`}>
                <span style={{ color: '#5C6B7A' }}>
                  Vehicle {i + 1}{v.rego ? ` — ${v.rego}` : v.description ? ` — ${v.description}` : ''} · {(v.type || '').replace(/_/g, ' ')}
                  {v.scale === 'heavy_motor' && <span className="ml-1 text-[9px] px-1 py-0.5 rounded font-bold" style={{ background: 'rgba(232,160,32,0.15)', color: '#B27516' }}>HM scale</span>}
                </span>
                <span className="font-mono" style={{ color: '#0D1B2A' }}>{fmt$(v.premium)}</span>
              </div>
            ))}
          </div>
        )}
        {q.components?.subtotal != null && (
          <Row label="Base premium (sum-insured rated)" value={fmt$(q.components.subtotal)} />
        )}
        {mods.map(m => (
          <Row key={m.label} label={m.label} value={`× ${Number(m.value).toFixed(2)}`}
            accent={m.value > 1 ? '#B27516' : m.value < 1 ? '#1D9E75' : '#5C6B7A'} />
        ))}
        <div className="border-t my-1.5" style={{ borderColor: 'rgba(136,150,168,0.15)' }} />
        {q.commission_adjusted && q.scheduled_gross_premium != null && (
          <Row label={`Scheduled gross premium (brokerage ${Math.round((q.scheduled_broker_commission_pct || 0) * 1000) / 10}%)`} value={fmt$(q.scheduled_gross_premium)} muted />
        )}
        <Row label={q.commission_adjusted ? 'Gross premium — at the brokerage set' : 'Gross premium'} value={fmt$(q.gross_premium)} bold />
        {q.minimum_premium_applied && <p className="text-[10px]" style={{ color: '#5C6B7A' }}>Minimum premium applied</p>}
        {q.commission_basis === 'not_recorded'
          ? <Row label="Brokerage" value="not recorded" muted />
          : q.broker_commission != null && (
            <Row label={q.commission_recorded && (q.broker_commission_pct || 0) <= 0 ? 'Brokerage — Nett (nil)'
              : `Brokerage (${Math.round((q.broker_commission_pct != null ? q.broker_commission_pct : q.broker_commission / (q.gross_premium || 1)) * 1000) / 10}%, included${q.commission_adjusted ? ` · schedule ${Math.round((q.scheduled_broker_commission_pct || 0) * 1000) / 10}%` : ''})`}
              value={fmt$(q.broker_commission)} muted accent={(q.broker_commission_pct || 0) <= 0 && q.commission_recorded ? '#B27516' : undefined} />
          )}
        {q.commission_recorded && q.net_premium != null && <Row label="MGA net premium" value={fmt$(q.net_premium)} muted />}
        {q.commission_basis === 'not_recorded' && <p className="text-[10px]" style={{ color: '#B27516' }} data-testid="quote-commission-not-recorded">No commission schedule recorded for this brokerage — Admin → Config → Commission schedule.</p>}
        <div className="border-t my-1.5" style={{ borderColor: 'rgba(136,150,168,0.15)' }} />
        {q.tax_apportioned ? (
          <>
            {(q.esl_levy || 0) > 0 && <Row label="Emergency services levy (NSW/TAS)" value={fmt$(q.esl_levy)} />}
            <Row label="GST (10%)" value={fmt$(q.gst)} />
            <Row label={`Stamp duty — apportioned (${(q.duty_lines || []).length ? q.duty_lines.map(d => `${d.state} ${d.percent}% @ ${d.rate_display}`).join(' / ') : Object.entries(q.tax_split || {}).map(([s, p]) => `${s} ${p}%`).join(' / ')})`} value={fmt$(q.stamp_duty)} />
            {(q.duty_exemptions || []).map(x => (
              <p key={x} className="text-[10px]" style={{ color: '#1D9E75' }} data-testid="duty-exemption-note">{x}</p>
            ))}
            <p className="text-[10px]" style={{ color: '#5C6B7A' }} data-testid="tax-engine-note">
              One engine: levy → GST → duty, apportioned by situation — matches the schedule and invoice to the cent.
            </p>
          </>
        ) : (
          <>
            <Row label={`Stamp duty (${(((q.stamp_duty_pct ?? (q.gross_premium ? (q.stamp_duty || 0) / q.gross_premium : 0))) * 100).toFixed(1)}%) — indicative, single-state basis`} value={fmt$(q.stamp_duty)} />
            <Row label={`GST (${((q.gst_pct || 0.1) * 100).toFixed(0)}%)`} value={fmt$(q.gst)} />
          </>
        )}
        <div className="border-t my-1.5" style={{ borderColor: 'rgba(136,150,168,0.15)' }} />
        <Row label="Total payable" value={fmt$(q.total_payable)} bold big />
      </div>
    </Card>
  );
}

const DIM_ADVICE = {
  'Claims History': 'Request detailed claims narratives and confirm remediation of loss causes.',
  'Financial': 'Obtain recent financials or a credit reference before binding.',
  'Location': 'Consider higher property excess or exclude flood/storm surge where exposed.',
  'Industry': 'Verify actual operations match the declared industry classification.',
  'Governance': 'Confirm director history and any regulatory actions directly with the client.',
};

function adviceFor(name) {
  const key = Object.keys(DIM_ADVICE).find(k => (name || '').toLowerCase().includes(k.toLowerCase()));
  return key ? DIM_ADVICE[key] : 'Gather supporting documentation for this factor to improve the score.';
}

function ExplainPanel({ sr }) {
  const dims = Object.entries(sr.dimension_scores || {}).map(([dim, d]) => ({ dim, ...d }));
  const totalMax = dims.reduce((s, d) => s + (d.max_points || 0), 0) || 1;
  const drivers = [...dims].sort((a, b) => (b.risk_factor || 0) - (a.risk_factor || 0)).slice(0, 4);
  const strengths = [...dims].sort((a, b) => (a.risk_factor || 0) - (b.risk_factor || 0)).slice(0, 3);
  const worst = drivers[0];
  const summary = `AIRA scored this risk ${sr.composite_score}/10000 → ${sr.decision}. ` +
    (worst ? `The biggest driver of risk points is ${worst.name} (${worst.score}/${worst.max_points} points, ${Math.round((worst.risk_factor || 0) * 100)}% of its maximum). ` : '') +
    (strengths[0] ? `The strongest aspect of this risk is ${strengths[0].name}. ` : '') +
    `${sr.decision_reason || ''}`;
  return (
    <div className="px-4 pb-4 space-y-4" data-testid="explain-panel">
      <p className="text-xs leading-relaxed p-3 rounded-sm" style={{ background: '#F5F7FA', color: '#0D1B2A' }}>{summary}</p>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#E24B4A' }}>Top risk drivers</p>
        {drivers.map(d => (
          <div key={d.dim} className="flex items-center gap-2 py-1 text-xs">
            <span className="w-6 font-mono font-bold" style={{ color: '#1A4C7C' }}>{d.dim}</span>
            <span className="flex-1" style={{ color: '#0D1B2A' }}>{d.name}</span>
            <span className="font-mono" style={{ color: '#5C6B7A' }}>weight {Math.round((d.max_points / totalMax) * 100)}%</span>
            <span className="font-mono font-semibold" style={{ color: (d.risk_factor || 0) > 0.5 ? '#E24B4A' : '#B27516' }}>
              {Math.round((d.risk_factor || 0) * 100)}% risk
            </span>
          </div>
        ))}
      </div>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#1D9E75' }}>Strengths</p>
        {strengths.map(d => (
          <div key={d.dim} className="flex items-center gap-2 py-1 text-xs">
            <span className="w-6 font-mono font-bold" style={{ color: '#1A4C7C' }}>{d.dim}</span>
            <span className="flex-1" style={{ color: '#0D1B2A' }}>{d.name}</span>
            <span className="font-mono" style={{ color: '#1D9E75' }}>{Math.round((d.risk_factor || 0) * 100)}% risk</span>
          </div>
        ))}
      </div>
      {drivers.some(d => (d.risk_factor || 0) > 0.4) && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#1A4C7C' }}>Suggested actions</p>
          {drivers.filter(d => (d.risk_factor || 0) > 0.4).map(d => (
            <p key={d.dim} className="text-xs py-0.5" style={{ color: '#5C6B7A' }}>• <b style={{ color: '#0D1B2A' }}>{d.name}:</b> {adviceFor(d.name)}</p>
          ))}
        </div>
      )}
    </div>
  );
}

